/**
 * Bundle options
 * -----------------------------------------------------------------------------
 * Turns the `properties[Option N]` selects rendered by snippets/bundle-options.liquid
 * into swatch pickers that show each component variant's image, and adds the
 * selected component variants to the cart alongside the main product as one
 * bundle: the main product first, then one line per chosen component.
 *
 * Each component line carries `parent_id` — the main product's variant id — and
 * no line item properties at all. That is what prices the component at zero and
 * nests it under the parent, and Shopify returns the result as
 * parent_relationship.parent_key, which is what the cart groups on.
 *
 * The native <select> stays in the DOM as the single source of truth, so the
 * line item properties, the no-JS fallback and any app reading those fields
 * keep working.
 */
(function () {
  'use strict';

  var SELECTORS = {
    container: '[data-bundle-options]',
    option: '[data-bundle-option]',
    select: '[data-bundle-select]',
    error: '[data-bundle-error]',
    variantMap: '[data-bundle-variant-map]',
    addToCart: '[data-add-to-cart]',
    productSelect: '[data-product-select]',
    swatch: '.bundle-swatch',
    // Standard product pages: the single variant dropdown gets the same picker.
    variantSwatch: '[data-variant-swatch]',
    section: '[data-section-type="product"]'
  };

  var CLASSES = {
    enhanced: 'bundle-option--enhanced',
    invalid: 'bundle-option--invalid',
    open: 'bundle-swatch--open',
    disabled: 'bundle-swatch__item--disabled',
    selected: 'bundle-swatch__item--selected',
    loading: 'btn--loading'
  };

  // Long scent lists get a filter box.
  var SEARCH_THRESHOLD = 12;

  var enhanced = typeof WeakSet === 'function' ? new WeakSet() : null;
  var openPicker = null;

  /* ==========================================================================
     Helpers
     ========================================================================== */

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function addUrl() {
    return routes().cartAdd || '/cart/add.js';
  }

  function cartUrl() {
    return routes().cartPage || '/cart';
  }

  function isDrawerCart() {
    return !!(window.theme && window.theme.settings && window.theme.settings.cartType === 'drawer');
  }

  // Bundle selects mark "nothing chosen" with an empty value; the theme's variant
  // dropdown uses "0". Each select declares its own via data-bundle-blank.
  function blankValue(select) {
    return select.getAttribute('data-bundle-blank') || '';
  }

  function optionsOf(select) {
    var blank = blankValue(select);
    return Array.prototype.filter.call(select.options, function (option) {
      return option.value !== blank;
    });
  }

  function soldOutLabel() {
    return (window.theme && window.theme.strings && window.theme.strings.soldOut) || 'Sold out';
  }

  // Product titles and image urls go into markup strings below, and Liquid does
  // not escape them on the way out.
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ==========================================================================
     Swatch picker
     ========================================================================== */

  function thumbMarkup(option) {
    var image = option.getAttribute('data-image');
    if (!image) {
      return '<span class="bundle-swatch__thumb bundle-swatch__thumb--empty" aria-hidden="true"></span>';
    }
    return '<span class="bundle-swatch__thumb"><img src="' + escapeHtml(image) + '" alt="" loading="lazy" width="48" height="48"></span>';
  }

  function itemMarkup(option, index) {
    var price = option.getAttribute('data-price');
    var disabled = option.disabled;

    return '<li class="bundle-swatch__item' + (disabled ? ' ' + CLASSES.disabled : '') + '"' +
      ' role="option" data-option-index="' + index + '"' +
      ' aria-selected="false"' + (disabled ? ' aria-disabled="true"' : '') + '>' +
      thumbMarkup(option) +
      '<span class="bundle-swatch__text">' +
        '<span class="bundle-swatch__name">' + escapeHtml(option.textContent.trim()) + '</span>' +
      '</span>' +
      (price ? '<span class="bundle-swatch__price">' + escapeHtml('') + '</span>' : '') +
      '</li>';
  }

  function buildPicker(optionEl, select) {
    var items = optionsOf(select);
    var placeholder = select.getAttribute('data-bundle-placeholder') || 'Choose an option';

    var picker = document.createElement('div');
    picker.className = 'bundle-swatch';

    var listMarkup = items
      .map(function (option) {
        return itemMarkup(option, Array.prototype.indexOf.call(select.options, option));
      })
      .join('');

    picker.innerHTML =
      '<button type="button" class="bundle-swatch__trigger" aria-haspopup="listbox" aria-expanded="false">' +
        '<span class="bundle-swatch__thumb bundle-swatch__thumb--empty" data-bundle-thumb aria-hidden="true"></span>' +
        '<span class="bundle-swatch__current" data-bundle-current></span>' +
        '<span class="bundle-swatch__caret" aria-hidden="true"></span>' +
      '</button>' +
      '<div class="bundle-swatch__panel" data-bundle-panel hidden>' +
        (items.length >= SEARCH_THRESHOLD
          ? '<div class="bundle-swatch__search">' +
              '<input type="search" class="bundle-swatch__search-input" data-bundle-search' +
              ' placeholder="' + escapeHtml('Search ' + items.length + ' options') + '" autocomplete="off">' +
            '</div>'
          : '') +
        '<ul class="bundle-swatch__list" role="listbox" data-bundle-list>' + listMarkup + '</ul>' +
      '</div>';

    picker.querySelector('[data-bundle-current]').textContent = placeholder;

    select.parentNode.insertBefore(picker, select.nextSibling);
    return picker;
  }

  function syncTrigger(picker, select) {
    var current = picker.querySelector('[data-bundle-current]');
    var thumb = picker.querySelector('[data-bundle-thumb]');
    var chosen = select.options[select.selectedIndex];
    var placeholder = select.getAttribute('data-bundle-placeholder') || 'Choose an option';

    if (!chosen || chosen.value === blankValue(select)) {
      current.textContent = placeholder;
      picker.classList.remove('bundle-swatch--has-value');
      thumb.className = 'bundle-swatch__thumb bundle-swatch__thumb--empty';
      thumb.innerHTML = '';
    } else {
      current.textContent = chosen.textContent.trim();
      picker.classList.add('bundle-swatch--has-value');

      var image = chosen.getAttribute('data-image');
      if (image) {
        thumb.className = 'bundle-swatch__thumb';
        thumb.innerHTML = '<img src="' + escapeHtml(image) + '" alt="" width="48" height="48">';
      } else {
        thumb.className = 'bundle-swatch__thumb bundle-swatch__thumb--empty';
        thumb.innerHTML = '';
      }
    }

    picker.querySelectorAll('.bundle-swatch__item').forEach(function (item) {
      var isSelected = !!chosen && String(item.dataset.optionIndex) === String(select.selectedIndex);
      item.classList.toggle(CLASSES.selected, isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  function closePicker(picker) {
    if (!picker) return;

    picker.classList.remove(CLASSES.open);
    picker.querySelector('[data-bundle-panel]').hidden = true;
    picker.querySelector('.bundle-swatch__trigger').setAttribute('aria-expanded', 'false');

    if (openPicker === picker) openPicker = null;
  }

  function filterList(picker, term) {
    var needle = term.trim().toLowerCase();

    picker.querySelectorAll('.bundle-swatch__item').forEach(function (item) {
      var text = item.textContent.toLowerCase();
      item.hidden = needle !== '' && text.indexOf(needle) === -1;
    });
  }

  // Checked at open time rather than cached at load, so a hybrid device that has
  // just been undocked or picked up answers for how it is being used right now.
  function canAutofocusSearch() {
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function openPanel(picker) {
    if (openPicker && openPicker !== picker) closePicker(openPicker);

    picker.classList.add(CLASSES.open);
    picker.querySelector('[data-bundle-panel]').hidden = false;
    picker.querySelector('.bundle-swatch__trigger').setAttribute('aria-expanded', 'true');
    openPicker = picker;

    var search = picker.querySelector('[data-bundle-search]');
    if (search) {
      search.value = '';
      filterList(picker, '');

      // Focusing the search box summons the on-screen keyboard on a phone, which
      // covers the very list the customer just opened. So it is only focused
      // where typing costs nothing: a real pointer with a hardware keyboard.
      // On touch the box is still there and still works — tapping it opens the
      // keyboard at the point the customer actually asks to type.
      if (canAutofocusSearch()) search.focus();
    }

    var selected = picker.querySelector('.' + CLASSES.selected);
    if (selected) selected.scrollIntoView({ block: 'nearest' });
  }

  function clearInvalid(optionEl) {
    optionEl.classList.remove(CLASSES.invalid);

    var error = optionEl.querySelector(SELECTORS.error);
    if (error) error.hidden = true;
  }

  function markInvalid(optionEl) {
    optionEl.classList.add(CLASSES.invalid);

    var error = optionEl.querySelector(SELECTORS.error);
    if (error) error.hidden = false;
  }

  function bindPicker(optionEl, select, picker) {
    var trigger = picker.querySelector('.bundle-swatch__trigger');
    var search = picker.querySelector('[data-bundle-search]');

    // The theme sets the variant dropdown itself on load, and a ?variant= link
    // moves it as well, so the trigger follows the select rather than assuming
    // this picker is the only thing that ever changes it.
    select.addEventListener('change', function () {
      syncTrigger(picker, select);
    });

    // product-template.liquid sets that select with jQuery's .val(...).change().
    // jQuery's trigger only runs jQuery-bound handlers, never native
    // addEventListener ones, so the listener above would miss it. Both are
    // registered and syncTrigger is idempotent, so a double call is harmless.
    if (window.jQuery) {
      window.jQuery(select).on('change', function () {
        syncTrigger(picker, select);
      });
    }

    trigger.addEventListener('click', function () {
      if (picker.classList.contains(CLASSES.open)) {
        closePicker(picker);
      } else {
        openPanel(picker);
      }
    });

    picker.querySelector('[data-bundle-list]').addEventListener('click', function (event) {
      var item = event.target.closest('.bundle-swatch__item');
      if (!item || item.classList.contains(CLASSES.disabled)) return;

      select.selectedIndex = parseInt(item.dataset.optionIndex, 10);
      select.dispatchEvent(new Event('change', { bubbles: true }));

      syncTrigger(picker, select);
      clearInvalid(optionEl);
      closePicker(picker);
      trigger.focus();
    });

    if (search) {
      search.addEventListener('input', function () {
        filterList(picker, search.value);
      });
    }

    picker.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;

      closePicker(picker);
      trigger.focus();
    });

    // Keep the picker honest if anything else writes to the select.
    select.addEventListener('change', function () {
      syncTrigger(picker, select);
      if (select.value !== '') clearInvalid(optionEl);
    });
  }

  function enhanceOption(optionEl) {
    var select = optionEl.querySelector(SELECTORS.select);
    if (!select) return;
    if (enhanced && enhanced.has(select)) return;

    // A cloned product form brings a dead picker with it — drop it before
    // building a live one.
    var stale = optionEl.querySelector(SELECTORS.swatch);
    if (stale) stale.parentNode.removeChild(stale);

    // JS owns validation from here on: a display:none select with `required`
    // would make the browser block submit on an element it cannot focus.
    select.removeAttribute('required');
    optionEl.classList.add(CLASSES.enhanced);

    var picker = buildPicker(optionEl, select);
    syncTrigger(picker, select);
    bindPicker(optionEl, select, picker);

    if (enhanced) enhanced.add(select);
  }

  /* ==========================================================================
     Variant-scoped metafields
     ========================================================================== */

  function rebuildSelect(select, components) {
    var placeholder = select.getAttribute('data-bundle-placeholder') || 'Choose an option';
    var previous = select.value;

    select.innerHTML = '';

    var blank = document.createElement('option');
    blank.value = '';
    blank.disabled = true;
    blank.selected = true;
    blank.textContent = placeholder;
    select.appendChild(blank);

    components.forEach(function (component) {
      var option = document.createElement('option');
      option.value = component.label;
      option.textContent = component.label + (component.available ? '' : ' (' + soldOutLabel() + ')');
      option.setAttribute('data-variant-id', component.id);
      if (component.price) option.setAttribute('data-price', component.price);
      if (component.image) option.setAttribute('data-image', component.image);
      option.disabled = !component.available;
      select.appendChild(option);
    });

    // Keep the shopper choice when the new list still offers it.
    if (previous) {
      Array.prototype.some.call(select.options, function (option) {
        if (option.value !== previous || option.disabled) return false;
        option.selected = true;
        return true;
      });
    }
  }

  function watchVariantChanges(container) {
    var mapEl = container.querySelector(SELECTORS.variantMap);
    if (!mapEl || mapEl.dataset.bundleWatched === 'true') return;

    var map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (error) {
      return;
    }

    mapEl.dataset.bundleWatched = 'true';

    var section = container.closest(SELECTORS.section) || document;

    section.addEventListener('variantChange', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant || !map[variant.id]) return;

      container.querySelectorAll(SELECTORS.option).forEach(function (optionEl) {
        var select = optionEl.querySelector(SELECTORS.select);
        if (!select) return;

        rebuildSelect(select, map[variant.id]);

        var picker = optionEl.querySelector(SELECTORS.swatch);
        if (!picker) return;

        picker.parentNode.removeChild(picker);
        if (enhanced) enhanced.delete(select);
        enhanceOption(optionEl);
      });
    });
  }

  /* ==========================================================================
     Add the bundle to the cart
     ========================================================================== */

  function mainVariantId(form) {
    var el = form.querySelector(SELECTORS.productSelect) ||
      form.querySelector('select[name="id"], input[name="id"]');

    if (!el && form.elements) el = form.elements.namedItem('id');

    return el && el.value ? el.value : null;
  }

  function quantity(form) {
    var field = form.elements ? form.elements.namedItem('quantity') : null;
    var value = parseInt(field && field.value, 10);

    return value > 0 ? value : 1;
  }

  function mainProperties(form) {
    var props = {};

    Array.prototype.forEach.call(form.elements || [], function (el) {
      if (!el.name) return;

      var match = el.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;

      var value = (el.value || '').trim();
      if (!value) return;

      props[match[1]] = value;
    });

    return props;
  }

  function validate(container) {
    var firstInvalid = null;

    container.querySelectorAll(SELECTORS.option).forEach(function (optionEl) {
      var select = optionEl.querySelector(SELECTORS.select);
      if (!select) return;

      if (select.value === '') {
        markInvalid(optionEl);
        if (!firstInvalid) firstInvalid = optionEl;
      } else {
        clearInvalid(optionEl);
      }
    });

    return firstInvalid;
  }

  function clearFormError(form) {
    var errors = form.querySelector('.errors');
    if (errors) errors.parentNode.removeChild(errors);
  }

  function showFormError(form, message) {
    clearFormError(form);

    var div = document.createElement('div');
    div.className = 'errors text-center';
    div.textContent = message;
    form.appendChild(div);

    document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
      detail: { errorMessage: message }
    }));
  }

  /**
   * Mirrors the delivery-protection auto-add that theme.AjaxProduct runs after a
   * normal add to cart, so bundle products behave like every other product.
   */
  function addDeliveryProtection() {
    var toggle = document.querySelector('[data-shipping-charge]');
    if (!toggle || !toggle.classList.contains('rw-unchecked')) return;

    var variantId = toggle.getAttribute('data-shipping-charge');
    if (!variantId) return;

    window.setTimeout(function () {
      fetch(addUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (response) {
          if (!response.ok) return;

          toggle.classList.add('rw-checked');
          toggle.classList.remove('rw-unchecked');
          toggle.querySelectorAll('[data-append-class]').forEach(function (el) {
            el.classList.add('rw-checked');
            el.classList.remove('rw-unchecked');
          });

          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .catch(function () {});
    }, 1000);
  }

  function bundleItems(form, container) {
    var qty = quantity(form);
    var props = mainProperties(form);
    var parentId = mainVariantId(form);
    var children = [];

    var randomBundleId = Math.floor(100000000 + Math.random() * 900000000);

    container.querySelectorAll(SELECTORS.option).forEach(function (optionEl) {
      var select = optionEl.querySelector(SELECTORS.select);
      if (!select) return;
      var randomBundleId_all = Math.floor(100000000 + Math.random() * 900000000);
      var chosen = select.options[select.selectedIndex];
      var componentId = chosen && chosen.getAttribute('data-variant-id');
      if (!componentId) return;

      // parent_id is the whole mechanism: it is the main product's selected
      // variant id, sent as a top-level field next to id and quantity, and it is
      // what gets the component priced at zero and nested under the parent.
      // Shopify hands the result back as parent_relationship.parent_key, whose
      // variant portion is this same id.
      //
      // No line item properties are sent. They were only ever a way to pair the
      // lines ourselves, and pairing is not the theme's job — it reads
      // parent_relationship. Properties are also returned by the Admin API even
      // when underscore-prefixed, which is how they were reaching ShipStation
      // and printing on packing slips.
      children.push({
        id: componentId,
        quantity: qty,
        parent_id: parentId,
        properties:{
          _random_number: randomBundleId_all,
        }
      });
    });

    // `props` is only what the customer typed into real properties[...] fields on
    // this product, if any. Nothing about the bundle is added to it.
    props._random_number = randomBundleId;
    return [{ id: parentId, quantity: qty, properties: props }].concat(children);
  }

  function submitBundle(form, container) {
    var button = form.querySelector(SELECTORS.addToCart);

    if (!mainVariantId(form)) {
      showFormError(form, 'Please choose a product option before adding to cart.');
      return;
    }

    var invalid = validate(container);
    if (invalid) {
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });

      var trigger = invalid.querySelector('.bundle-swatch__trigger');
      if (trigger) trigger.focus();
      return;
    }

    clearFormError(form);
    if (button) button.classList.add(CLASSES.loading);

    fetch(addUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: bundleItems(form, container) })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (button) button.classList.remove(CLASSES.loading);

        if (!result.ok || result.data.status) {
          showFormError(
            form,
            result.data.description || result.data.message || 'This bundle could not be added to your cart.'
          );
          return;
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: result.data, addToCartBtn: button }
        }));

        addDeliveryProtection();

        if (!isDrawerCart()) {
          window.location.href = cartUrl();
        } else if (document.body.classList.contains('template-cart')) {
          window.scrollTo(0, 0);
          window.location.reload();
        }
      })
      .catch(function () {
        if (button) button.classList.remove(CLASSES.loading);
        showFormError(form, 'Something went wrong adding this bundle to your cart. Please try again.');
      });
  }

  /**
   * Captured on `document` so it runs before the submit handler theme.AjaxProduct
   * binds to the form, which can only add a single line item.
   */
  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var container = form.querySelector(SELECTORS.container);
    if (!container) return;

    // The dynamic checkout button, Buy it now and Shop Pay all submit this same
    // form. Cancelling those turns a checkout into an add-to-cart, so only an
    // explicit press of the add-to-cart button is taken over.
    //
    // event.submitter is undefined on browsers that do not implement it, and null
    // on a programmatic requestSubmit(). Both of those fall through to the theme
    // rather than being hijacked.
    if ('submitter' in event) {
      var submitter = event.submitter;
      if (!submitter) return;
      if (submitter.name !== 'add' && !submitter.closest(SELECTORS.addToCart)) return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    submitBundle(form, container);
  }

  /* ==========================================================================
     Init
     ========================================================================== */

  function init(root) {
    var scope = root || document;

    scope.querySelectorAll(SELECTORS.container).forEach(function (container) {
      container.querySelectorAll(SELECTORS.option).forEach(enhanceOption);
      watchVariantChanges(container);
    });

    // Standard product pages. The wrapper is passed straight in: enhanceOption
    // only needs an element containing a [data-bundle-select].
    scope.querySelectorAll(SELECTORS.variantSwatch).forEach(enhanceOption);
  }

  function onDocumentClick(event) {
    if (!openPicker) return;
    if (event.target.closest(SELECTORS.swatch) === openPicker) return;

    closePicker(openPicker);
  }

  function start() {
    init();

    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });

    // Product forms can arrive late (quick shop, apps that clone the form).
    if (typeof MutationObserver === 'function') {
      new MutationObserver(function (mutations) {
        var needsInit = mutations.some(function (mutation) {
          return Array.prototype.some.call(mutation.addedNodes, function (node) {
            if (node.nodeType !== 1) return false;
            return node.matches(SELECTORS.container) ||
              node.querySelector(SELECTORS.container) ||
              node.matches(SELECTORS.variantSwatch) ||
              node.querySelector(SELECTORS.variantSwatch);
          });
        });

        if (needsInit) init();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.theme = window.theme || {};
  window.theme.bundleOptions = { init: init };
})();
